"use client";

import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from '@/components/ui/badge';
import { 
  Cookie, 
  Globe, 
  Clock, 
  Shield, 
  Database, 
  Eye, 
  FileText, 
  Zap, 
  Users, 
  BarChart,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info
} from 'lucide-react';

export default function AboutCookiesPage() {
  const t = useTranslations('aboutCookies');
  const params = useParams();
  const locale = params?.locale as string || 'sv';

  const cookieTypes = [
    {
      id: "necessary",
      icon: <Shield className="h-6 w-6 text-green-600" />,
      color: "green",
      required: true,
      examples: ["Session management", "Authentication", "Security", "Form data"]
    },
    {
      id: "preference",
      icon: <Users className="h-6 w-6 text-blue-600" />,
      color: "blue", 
      required: false,
      examples: ["Language selection", "Theme (dark/light)", "Layout preferences", "Accessibility options"]
    },
    {
      id: "analytics",
      icon: <BarChart className="h-6 w-6 text-purple-600" />,
      color: "purple",
      required: false,
      examples: ["Google Analytics", "Page views", "User flows", "Performance measurement"]
    },
    {
      id: "marketing",
      icon: <Eye className="h-6 w-6 text-orange-600" />,
      color: "orange",
      required: false,
      examples: ["Facebook Pixel", "Google Ads", "Retargeting", "Conversion tracking"]
    }
  ];

  const cookieDurations = [
    {
      id: "session",
      icon: <Clock className="h-5 w-5 text-blue-600" />,
    },
    {
      id: "persistent",
      icon: <Database className="h-5 w-5 text-purple-600" />,
    }
  ];

  // Helper function to get example list for a cookie type
  const getExamples = (cookieTypeId: string) => {
    const cookieType = cookieTypes.find(type => type.id === cookieTypeId);
    return cookieType?.examples || [];
  };

  // Helper function to get usage list for party type
  const getPartyUsages = (partyType: string) => {
    if (partyType === "firstParty") {
      return ["Keeping you logged in", "Remembering language choices", "Saving shopping cart", "Website settings"];
    } else {
      return ["Targeted advertising", "Cross-site tracking", "Social media buttons", "Analytics tools"];
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-white dark:bg-gray-950">
        <div className="container px-4 md:px-6 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center rounded-full px-4 py-2 text-sm font-medium bg-blue-500/10 text-blue-700 dark:text-blue-300 w-fit mx-auto mb-6">
              <Cookie className="h-4 w-4 mr-2" />
              {t('hero.badge')}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-gray-900 dark:text-white">
              {t('hero.title')} <span className="text-blue-600">{t('hero.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              {t('hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* What Are Cookies */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container px-4 md:px-6 max-w-6xl mx-auto">
          <div className="grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white">
                {t('whatAreCookies.title')}
              </h2>
              <div className="space-y-4 text-lg text-gray-600 dark:text-gray-400">
                <p>{t.rich('whatAreCookies.description1', {
                  strong: (chunks) => <strong className="text-gray-900 dark:text-white">{chunks}</strong>
                })}</p>
                <p>{t('whatAreCookies.description2')}</p>
                <p>{t('whatAreCookies.description3')}</p>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 shadow-lg">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('whatAreCookies.example.title')}</span>
                    <Badge variant="outline" className="text-xs">{t('whatAreCookies.example.badge')}</Badge>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-md p-4 font-mono text-sm">
                    <div className="text-blue-600 dark:text-blue-400">session_id=abc123xyz</div>
                    <div className="text-green-600 dark:text-green-400">language=sv</div>
                    <div className="text-purple-600 dark:text-purple-400">theme=dark</div>
                    <div className="text-orange-600 dark:text-orange-400">cart_items=3</div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('whatAreCookies.example.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Cookies Work */}
      <section className="py-16 bg-white dark:bg-gray-950">
        <div className="container px-4 md:px-6 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              {t('howCookiesWork.title')}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              {t('howCookiesWork.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <Card className="text-center">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-blue-600">1</span>
                </div>
                <CardTitle>{t('howCookiesWork.steps.firstVisit.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 dark:text-gray-400">
                  {t('howCookiesWork.steps.firstVisit.description')}
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-purple-600">2</span>
                </div>
                <CardTitle>{t('howCookiesWork.steps.storage.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 dark:text-gray-400">
                  {t('howCookiesWork.steps.storage.description')}
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-green-600">3</span>
                </div>
                <CardTitle>{t('howCookiesWork.steps.revisit.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 dark:text-gray-400">
                  {t('howCookiesWork.steps.revisit.description')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Domain-based Storage Example */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-8">
            <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white text-center">
              {t('howCookiesWork.domainStorage.title')}
            </h3>
            
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="bg-white dark:bg-gray-800">
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Globe className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-lg">{t('howCookiesWork.domainStorage.newUser.title')}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                      <span className="text-sm">{t('howCookiesWork.domainStorage.newUser.mapStatus')}</span>
                      <XCircle className="h-4 w-4 text-red-500" />
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <strong>{t('howCookiesWork.domainStorage.newUser.result')}</strong> {t('howCookiesWork.domainStorage.newUser.resultText')}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-gray-800">
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Globe className="h-5 w-5 text-green-600" />
                    <CardTitle className="text-lg">{t('howCookiesWork.domainStorage.googleUser.title')}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                      <span className="text-sm">{t('howCookiesWork.domainStorage.googleUser.mapStatus')}</span>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <strong>{t('howCookiesWork.domainStorage.googleUser.result')}</strong> {t('howCookiesWork.domainStorage.googleUser.resultText')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start space-x-3">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                    {t('howCookiesWork.domainStorage.importantInfo.title')}
                  </h4>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    {t.rich('howCookiesWork.domainStorage.importantInfo.description', {
                      strong: (chunks) => <strong>{chunks}</strong>
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Cookies */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container px-4 md:px-6 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              {t('cookieTypes.title')}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              {t('cookieTypes.subtitle')}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {cookieTypes.map((cookie) => (
              <Card key={cookie.id} className="bg-white dark:bg-gray-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {cookie.icon}
                      <CardTitle className="text-xl">{t(`cookieTypes.types.${cookie.id}.type`)}</CardTitle>
                    </div>
                    {cookie.required ? (
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                        {t('cookieTypes.types.necessary.required')}
                      </Badge>
                    ) : (
                      <Badge variant="outline">{t('cookieTypes.types.necessary.optional')}</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {t(`cookieTypes.types.${cookie.id}.description`)}
                  </p>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{t('cookieTypes.types.examplesTitle')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {getExamples(cookie.id).map((example, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {example}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Cookie Duration */}
      <section className="py-16 bg-white dark:bg-gray-950">
        <div className="container px-4 md:px-6 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              {t('cookieDuration.title')}
            </h2>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            {cookieDurations.map((duration) => (
              <Card key={duration.id} className="bg-white dark:bg-gray-800">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    {duration.icon}
                    <CardTitle className="text-xl">{t(`cookieDuration.types.${duration.id}.title`)}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                      <span className="font-medium text-gray-900 dark:text-white">{t('cookieDuration.types.durationLabel')}</span>
                      <span className="text-gray-600 dark:text-gray-400">{t(`cookieDuration.types.${duration.id}.duration`)}</span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">
                      {t(`cookieDuration.types.${duration.id}.description`)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* First-party vs Third-party */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container px-4 md:px-6 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              {t('cookieParties.title')}
            </h2>
          </div>

          <div className="grid gap-8 lg:grid-cols-2">
            <Card className="bg-white dark:bg-gray-800">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <Shield className="h-6 w-6 text-green-600" />
                  <CardTitle className="text-xl">{t('cookieParties.firstParty.title')}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-gray-600 dark:text-gray-400">
                    {t('cookieParties.firstParty.description')}
                  </p>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{t('cookieParties.firstParty.usageTitle')}</h4>
                    <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      {getPartyUsages("firstParty").map((usage, index) => (
                        <li key={index}>{usage}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white dark:bg-gray-800">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <AlertTriangle className="h-6 w-6 text-orange-600" />
                  <CardTitle className="text-xl">{t('cookieParties.thirdParty.title')}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-gray-600 dark:text-gray-400">
                    {t('cookieParties.thirdParty.description')}
                  </p>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{t('cookieParties.thirdParty.usageTitle')}</h4>
                    <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      {getPartyUsages("thirdParty").map((usage, index) => (
                        <li key={index}>{usage}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Privacy and Control */}
      <section className="py-16 bg-white dark:bg-gray-950">
        <div className="container px-4 md:px-6 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              {t('privacyControl.title')}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              {t('privacyControl.subtitle')}
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <CardHeader>
                <FileText className="h-8 w-8 text-blue-600 mb-2" />
                <CardTitle>{t('privacyControl.gdprRights.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 dark:text-gray-400">
                  {t('privacyControl.gdprRights.description')}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
              <CardHeader>
                <Zap className="h-8 w-8 text-green-600 mb-2" />
                <CardTitle>{t('privacyControl.easyChoices.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 dark:text-gray-400">
                  {t('privacyControl.easyChoices.description')}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
              <CardHeader>
                <Database className="h-8 w-8 text-purple-600 mb-2" />
                <CardTitle>{t('privacyControl.manageCookies.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 dark:text-gray-400">
                  {t('privacyControl.manageCookies.description')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Common Questions */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container px-4 md:px-6 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              {t('faq.title')}
            </h2>
          </div>

          <div className="space-y-6">
            {['dangerous', 'blockAll', 'delete', 'banners'].map((questionKey) => (
              <Card key={questionKey} className="bg-white dark:bg-gray-800">
                <CardHeader>
                  <CardTitle className="text-lg">{t(`faq.questions.${questionKey}.question`)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t(`faq.questions.${questionKey}.answer`)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white dark:bg-gray-950">
        <div className="container px-4 md:px-6 max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
            {t('cta.description')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-medium" asChild>
              <Link href={`/${locale}/signup`}>
                {t('cta.getStarted')}
               </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link href={`/${locale}/features`}>
                {t('cta.readMore')}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}